/**
 * 自定義滾動觀察器 Composable（用於需要特殊處理的頁面）
 * 
 * 功能：
 * 1. 初始化 Locomotive Scroll
 * 2. 提供 Intersection Observer 實例供自定義使用
 * 3. 自動清理資源
 * 
 * 使用場景：
 * - 需要觀察特定 ref 元素
 * - 需要自定義動畫延遲
 * - 需要更細粒度的控制
 */

import { onMounted, onUnmounted } from 'vue'

export const useCustomScrollObserver = () => {
  // Locomotive Scroll 實例
  let locomotiveScroll: any = null
  
  // Intersection Observer 實例
  let observer: IntersectionObserver | null = null

  /**
   * 初始化 Locomotive Scroll
   */
  const initLocomotiveScroll = async () => {
    try {
      // @ts-ignore - locomotive-scroll 缺少類型定義
      const LocomotiveScroll = (await import('locomotive-scroll')).default

      const scrollContainer = document.querySelector('[data-scroll-container]')

      if (scrollContainer) {
        locomotiveScroll = new LocomotiveScroll({
          el: scrollContainer as HTMLElement,
          smooth: false,
          smoothMobile: false,
          inertia: 0.8,
          multiplier: 1.0,
          class: 'is-inview',
          scrollFromAnywhere: true,
          getDirection: true,
          getSpeed: true,
        })
      }
    } catch (error) {
      console.error('Failed to initialize Locomotive Scroll:', error)
    }
  }

  /**
   * 創建 Intersection Observer
   */
  const createObserver = () => {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-inview')
          } else {
            entry.target.classList.remove('is-inview')
          }
        })
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -100px 0px',
      }
    )
    return observer
  }

  /**
   * 清理資源
   */
  const cleanup = () => {
    if (locomotiveScroll) {
      locomotiveScroll.destroy()
      locomotiveScroll = null
    }

    if (observer) {
      observer.disconnect()
      observer = null
    }
  }

  // 組件掛載時初始化
  onMounted(async () => {
    if (typeof window === 'undefined') return
    await initLocomotiveScroll()
  })

  // 組件卸載時清理
  onUnmounted(() => {
    cleanup()
  })

  return {
    locomotiveScroll,
    createObserver,
    cleanup,
  }
}

